import React, { useState } from 'react';
import { usePage, useForm, Link } from '@inertiajs/react';

export default function TemplateCreate() {
  const { whatsappTemplate, isEdit, errors, flash } = usePage<any>().props;

  const { data, setData, post, put, processing } = useForm({
    whatsapp_template: {
      template_name: whatsappTemplate?.template_name || '',
      language_code: whatsappTemplate?.language_code || 'en_US',
      template_category: whatsappTemplate?.template_category || 'marketing',
      header_format: whatsappTemplate?.header_format || 'no_header',
      header_text: whatsappTemplate?.header_text || '',
      header_example: whatsappTemplate?.header_example || '',
      template_body: whatsappTemplate?.template_body || '',
      footer_text: whatsappTemplate?.footer_text || '',
      header_file: null as File | null,
      whatsapp_template_buttons_attributes: whatsappTemplate?.whatsapp_template_buttons || [],
    }
  });

  const addButton = () => {
    const activeLength = data.whatsapp_template.whatsapp_template_buttons_attributes.filter((b: any) => !b._destroy).length;
    const defaultBtn = { button_format: 'quick_reply', button_text: '', cta_format: 'no_action', cta_value: '', position: activeLength };
    updateField('whatsapp_template_buttons_attributes', [...data.whatsapp_template.whatsapp_template_buttons_attributes, defaultBtn]);
  };

  const removeButton = (index: number) => {
    const newButtons = [...data.whatsapp_template.whatsapp_template_buttons_attributes];
    if (newButtons[index].id) {
      newButtons[index]._destroy = 1;
    } else {
      newButtons.splice(index, 1);
    }
    
    let pos = 0;
    newButtons.forEach((btn: any) => {
      if (!btn._destroy) {
        btn.position = pos++;
      }
    });
    updateField('whatsapp_template_buttons_attributes', newButtons);
  };

  const updateButton = (index: number, field: string, value: any) => {
    const newButtons = [...data.whatsapp_template.whatsapp_template_buttons_attributes];
    newButtons[index] = { ...newButtons[index], [field]: value };
    updateField('whatsapp_template_buttons_attributes', newButtons);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const locale = window.location.pathname.split('/')[1] || 'en';
    if (isEdit) {
      put(`/${locale}/whatsapp_templates/${whatsappTemplate.id}`, {
        forceFormData: !!data.whatsapp_template.header_file,
      });
    } else {
      post(`/${locale}/whatsapp_templates`, {
        forceFormData: true,
      });
    }
  };

  const updateField = (field: string, value: any) => {
    setData('whatsapp_template', {
      ...data.whatsapp_template,
      [field]: value
    });
  };

  return (
    <div className="max-w-screen-md mx-auto py-10 px-4 md:px-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">
          {isEdit ? 'Edit Template' : 'Create New WhatsApp Template'}
        </h1>

        <Link href={`/${window.location.pathname.split('/')[1] || 'en'}/whatsapp_templates`} className="text-gray-500 hover:text-gray-700">Back</Link>
      </div>

      {(flash?.alert || errors?.base) && (
        <div className="bg-red-50 text-red-600 p-4 rounded mb-6 border border-red-200">
          {flash?.alert || errors?.base}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
              <input 
                type="text" 
                value={data.whatsapp_template.template_name}
                onChange={e => updateField('template_name', e.target.value)}
                required
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                placeholder="e.g. welcome_message"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select 
                value={data.whatsapp_template.language_code}
                onChange={e => updateField('language_code', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              >
                <option value="en_US">English (en_US)</option>
                <option value="ar">Arabic (ar)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select 
                value={data.whatsapp_template.template_category}
                onChange={e => updateField('template_category', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              >
                <option value="marketing">Marketing</option>
                <option value="utility">Utility</option>
                <option value="authentication">Authentication</option>
              </select>
            </div>
          </div>

          <hr className="border-gray-200" />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Header Format</label>
            <select 
              value={data.whatsapp_template.header_format}
              onChange={e => updateField('header_format', e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm mb-3"
            >
              <option value="no_header">None</option>
              <option value="text_header">Text</option>
              <option value="image_header">Image</option>
              <option value="document_header">Document</option>
              <option value="video_header">Video</option>
            </select>
            
            {data.whatsapp_template.header_format === 'text_header' && (
              <div className="space-y-4">
                <div>
                  <input 
                    type="text" 
                    value={data.whatsapp_template.header_text}
                    onChange={e => updateField('header_text', e.target.value)}
                    placeholder="Header Text (Max 60 chars)"
                    maxLength={60}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Can include 1 variable e.g. {'{{1}}'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Header Example</label>
                  <input 
                    type="text" 
                    value={data.whatsapp_template.header_example}
                    onChange={e => updateField('header_example', e.target.value)}
                    placeholder="e.g. John"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Required if header contains a variable.</p>
                </div>
              </div>
            )}
            {['image_header', 'document_header', 'video_header'].includes(data.whatsapp_template.header_format) && (
              <input 
                type="file" 
                onChange={e => updateField('header_file', e.target.files ? e.target.files[0] : null)}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message Body</label>
            <textarea 
              value={data.whatsapp_template.template_body}
              onChange={e => updateField('template_body', e.target.value)}
              required
              rows={4}
              placeholder="e.g. Hello {{1}}, welcome to {{2}}!"
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">Use double brackets for variables, e.g., {'{{1}}'}, {'{{2}}'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text (Optional)</label>
            <input 
              type="text" 
              value={data.whatsapp_template.footer_text}
              onChange={e => updateField('footer_text', e.target.value)}
              maxLength={60}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            />
          </div>

          <hr className="border-gray-200" />
          
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="block text-sm font-medium text-gray-700">Interactive Buttons</h3>
              <button 
                type="button" 
                onClick={addButton}
                disabled={data.whatsapp_template.whatsapp_template_buttons_attributes.filter((b: any) => !b._destroy).length >= 3}
                className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 py-1 px-3 rounded shadow-sm disabled:opacity-50"
              >
                + Add Button
              </button>
            </div>
            
            <div className="space-y-4">
              {data.whatsapp_template.whatsapp_template_buttons_attributes.map((btn: any, index: number) => {
                if (btn._destroy) return null;
                return (
                  <div key={btn.id || index} className="p-4 border border-gray-200 rounded-md bg-gray-50 relative">
                    <button 
                      type="button" 
                      onClick={() => removeButton(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Button Type</label>
                        <select 
                          value={btn.button_format}
                          onChange={e => {
                            const val = e.target.value;
                            updateButton(index, 'button_format', val);
                            if (val === 'quick_reply') {
                              updateButton(index, 'cta_format', 'no_action');
                              updateButton(index, 'cta_value', '');
                            } else if (!btn.cta_format || btn.cta_format === 'no_action') {
                              updateButton(index, 'cta_format', 'url');
                            }
                          }}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        >
                          <option value="quick_reply">Quick Reply</option>
                          <option value="call_to_action">Call to Action</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Button Text</label>
                        <input 
                          type="text" 
                          value={btn.button_text || ''}
                          onChange={e => updateButton(index, 'button_text', e.target.value)}
                          maxLength={25}
                          required
                          placeholder="e.g. Stop"
                          className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                    </div>
                    
                    {btn.button_format === 'call_to_action' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-200 pt-3 mt-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Action Type</label>
                          <select 
                            value={btn.cta_format || 'url'}
                            onChange={e => updateButton(index, 'cta_format', e.target.value)}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          >
                            <option value="url">Website URL</option>
                            <option value="phone">Phone Number</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Action Value</label>
                          <input 
                            type="text" 
                            value={btn.cta_value || ''}
                            onChange={e => updateButton(index, 'cta_value', e.target.value)}
                            required
                            placeholder={btn.cta_format === 'phone' ? '+14155552671' : 'https://example.com'}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {data.whatsapp_template.whatsapp_template_buttons_attributes.filter((b: any) => !b._destroy).length === 0 && (
                <p className="text-sm text-gray-500 italic py-2">No buttons added. You can add up to 3 buttons.</p>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
            <Link href={`/${window.location.pathname.split('/')[1] || 'en'}/whatsapp_templates`} className="bg-white border border-gray-300 text-gray-700 font-medium py-2 px-4 rounded shadow-sm hover:bg-gray-50">
              Cancel
            </Link>
            <button 
              type="submit" 
              disabled={processing}
              className="bg-primary hover:bg-primary-dark text-white font-medium py-2 px-6 rounded shadow-sm disabled:opacity-50"
            >
              {processing ? 'Submitting...' : isEdit ? 'Update Template' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
